/**
 * Per-property FAQ generator. Templates 10-13 Q&A pairs from the
 * property document itself — no admin input, no per-listing content
 * backfill. Each property gets a fully populated FAQ section
 * automatically.
 *
 * Pairs with components/PropertyFAQSection.tsx (renders the visible
 * accordion) and lib/schemas.faqPageSchema (emits per-listing
 * FAQPage JSON-LD for "People Also Ask" and AI Overview citations).
 *
 * Conditional questions (location, tenure, TOP, developer, units,
 * price) are skipped when the underlying data is missing or
 * placeholder, so a half-populated property doesn't ship awkward
 * "comprises 0 units" answers. CTAs (showflat, VIP viewing, etc.)
 * render on every listing.
 */

import { DISTRICT_LIST } from "@/constant/variables";
import { isExecutiveCondo } from "@/lib/v2/propertyKind";

export interface PropertyForFAQ {
  name: string;
  title?: string;
  property_type?: string | null;
  /**
   * Set by callers that rename a project for display, where the stored name
   * carries the "EC" token but the displayed one does not. Left unset, the
   * kind is derived from the record itself.
   */
  isExecutiveCondo?: boolean;
  full_address?: string;
  district?: string;
  is_leasehold?: boolean;
  lease_year?: number | null;
  construction_status?: string;
  project_developer?: string;
  units?: number | null;
  min_flat_price?: number | null;
  max_flat_price?: number | null;
}

export interface PropertyFAQ {
  question: string;
  answer: string;
}

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `S$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `S$${(n / 1_000).toFixed(0)}K`;
  return `S$${n.toLocaleString()}`;
}

function districtPhrase(code: string): string {
  const d = DISTRICT_LIST.find((x) => x.value === code);
  return d?.label ? `District ${code.replace(/^D/, "")} (${d.label})` : code;
}

/**
 * Policy block for executive condominium pages.
 *
 * Sources, all checked 30/09/2026:
 *  - HDB, Eligibility for buying an EC: citizenship and age conditions, and
 *    the income ceiling split ($18,000 for sites with tenders closing on or
 *    after 24 Aug 2026, $16,000 for sites awarded before that).
 *  - HDB, Conditions after buying an EC: MOP computed from the date of the
 *    Temporary Occupation Permit, 10 years for sites whose tender closed on
 *    or after 8 May 2026 and 5 years otherwise, resale levy of $55,000 for
 *    families and $27,500 for singles.
 *  - MND, 8 May 2026: the 10-year MOP, the withdrawal of the Deferred
 *    Payment Scheme and privatisation moving to year 15.
 *
 * Deliberately no per-project MOP or ceiling figure: both depend on the
 * site's tender closing date, which the record does not carry. The answers
 * explain the rule and say where to check, rather than guessing.
 */
/** Applies to both an EC and a private condo: the MCST bill is the same idea. */
function maintenanceFeeFaq(name: string): PropertyFAQ {
  return {
    question: `What are the typical maintenance fees at ${name}?`,
    answer: `Monthly maintenance fees at ${name} are payable to the Management Corporation Strata Title (MCST) once the development is completed. Across Singapore, mass-market new launches typically fall between S$300 and S$800 per month for a 3-bedroom unit, while developments with extensive facilities run higher. What you pay depends on your unit's share value, the size of the development and how much there is to maintain, so a small project with a full facilities deck often costs more per household than a large one. The first year's contribution is usually paid in advance at completion. We will send the exact MCST budget for ${name} once the developer publishes it.`,
  };
}

function executiveCondoFaqs(name: string): PropertyFAQ[] {
  return [
    {
      question: `Who can buy ${name}?`,
      answer: `${name} is an executive condominium (EC), so HDB's eligibility rules apply. You need at least one Singapore Citizen applicant, with the other applicants being citizens or Permanent Residents, applying under an eligible scheme such as the Public Scheme or the Fiancé/Fiancée Scheme. Your household must be within the income ceiling, must not own private property or have disposed of any in the last 30 months, and must still have housing subsidy eligibility. Foreigners cannot buy a new EC, and neither can a household made up only of Permanent Residents. Check your own case against <a href="https://www.hdb.gov.sg/buying-a-flat/executive-condominiums/eligibility" target="_blank" rel="noopener noreferrer">HDB's EC eligibility page</a> before you commit.`,
    },
    {
      question: `What is the income ceiling for ${name}?`,
      answer: `The EC income ceiling depends on when the site's land tender closed, not on when you buy. HDB sets it at S$18,000 a month for EC projects with tenders closing on or after 24 August 2026, and S$16,000 for sites awarded before that date. Ask us which applies to ${name}, or read <a href="https://aiproperty.sg/blog/ec-income-ceiling-raised-18000-2026">how the ceiling changed in August 2026</a>.`,
    },
    {
      question: `What is the Minimum Occupation Period (MOP) at ${name}?`,
      answer: `It is either 5 or 10 years, set by the site's land tender date. EC sites whose tenders closed on or after 8 May 2026 carry a 10-year MOP, and everything tendered before that keeps 5 years. HDB counts the MOP from the date of the Temporary Occupation Permit, not from when you sign or collect keys. During the MOP you cannot sell the unit, rent out the whole unit, or buy another residential property. Our guide to <a href="https://aiproperty.sg/blog/ec-mop-resale-ec-guide">EC MOP and resale rules</a> sets out both timelines.`,
    },
    {
      question: `When can ${name} be sold to a foreigner?`,
      answer: `Only after full privatisation. For EC sites tendered before 8 May 2026 that is 10 years from the Temporary Occupation Permit, and for later sites it is 15 years. Between the end of the MOP and privatisation you can sell only to Singapore Citizens and Permanent Residents.`,
    },
    {
      question: `Can I use an HDB loan or CPF for ${name}?`,
      answer: `You cannot use an HDB housing loan for an EC, because an EC is sold by a private developer. You will need a bank loan of up to 75% of the price, with at least 5% of the price paid in cash. You can use CPF Ordinary Account savings for the rest of the downpayment and for the monthly instalments. Work the numbers through our <a href="https://aiproperty.sg/mortgage-calculator">mortgage calculator</a>.`,
    },
    {
      question: `Does the Mortgage Servicing Ratio apply to ${name}?`,
      answer: `Yes. Buying a new EC from a developer is capped by the Mortgage Servicing Ratio, so the monthly repayment cannot exceed 30% of your gross monthly income. The Total Debt Servicing Ratio of 55%, which counts all your other debts as well, applies on top. Whichever limit you hit first sets the loan you can take.`,
    },
    {
      question: `How does the booking and payment process work at ${name}?`,
      answer: `Register your interest on this page and we will send the price list, floor plans and unit availability as soon as the developer releases them. When you book, you pay a 5% booking fee and the developer issues the Option to Purchase. A further 15% is due within 8 weeks, then payments follow construction milestones under the Normal Payment Scheme. Some older EC sites can still offer the Deferred Payment Scheme, which was withdrawn for sites tendered from 8 May 2026, so ask which applies here.`,
    },
    {
      question: `Do I pay the resale levy on ${name}?`,
      answer: `Only if you are a second-timer, meaning your household has already taken a housing subsidy. Buying a new EC from a developer counts as taking a second subsidy, so the levy applies: HDB sets it at S$55,000 for families and S$27,500 for singles. Buying a resale EC on the open market does not attract the levy, because that purchase carries no fresh subsidy.`,
    },
    {
      question: `How is an EC different from a private condominium?`,
      answer: `The building and the facilities are private, but the first years come with public-housing conditions. An EC has an income ceiling, an eligibility check, an MOP, and restrictions on who you can sell to until it is privatised. In exchange, developers price ECs below comparable private condos, which the Ministry of National Development puts at roughly 20% to 30%. A private condo has none of those conditions and no ceiling, but you pay the market price from day one.`,
    },
  ];
}

export function generatePropertyFAQ(p: PropertyForFAQ): PropertyFAQ[] {
  const name = p.title || p.name;
  const faqs: PropertyFAQ[] = [];

  // 1. Location
  if (p.full_address || p.district) {
    // Built as a sentence, not a comma-joined list: the old version
    // produced "X is located, in District 25 of Singapore" whenever the
    // address was missing.
    const where: string[] = [];
    if (p.full_address) where.push(`at ${p.full_address}`);
    if (p.district) where.push(`in ${districtPhrase(p.district)} of Singapore`);
    faqs.push({
      question: `Where is ${name} located?`,
      answer: `${name} is located ${where.join(", ")}.`,
    });
  }

  // 2. Tenure
  if (p.is_leasehold === false) {
    faqs.push({
      question: `Is ${name} leasehold or freehold?`,
      answer: `${name} is a freehold development. Owners hold the property and the land beneath it in perpetuity, with no fixed lease term.`,
    });
  } else if (p.is_leasehold === true) {
    const term = p.lease_year || 99;
    if (term === 999) {
      faqs.push({
        question: `Is ${name} leasehold or freehold?`,
        answer: `${name} is a 999-year leasehold development. For most practical ownership timelines, a 999-year lease is functionally similar to freehold.`,
      });
    } else {
      faqs.push({
        question: `Is ${name} leasehold or freehold?`,
        answer: `${name} is a ${term}-year leasehold development. Under Singapore's leasehold system, the land reverts to the State at the end of the lease term.`,
      });
    }
  }

  // 3. TOP / construction status
  if (p.construction_status) {
    const s = p.construction_status;
    let answer = "";
    if (s === "Move In" || s === "Completed") {
      answer = `${name} has received its Temporary Occupation Permit (TOP) and is ready for move-in. Units are available for immediate occupancy.`;
    } else if (s === "Under Construction") {
      answer = `${name} is currently under construction. The expected TOP date is set by the developer; register your interest on this page to be notified of construction milestones and the official TOP announcement.`;
    } else if (s === "Upcoming") {
      answer = `${name} is an upcoming launch. The official TOP date will be confirmed closer to the project's public launch.`;
    } else if (s === "Sold Out") {
      answer = `${name} is sold out. TOP details are confirmed in the project's original sales materials.`;
    } else {
      answer = `${name} is currently in the ${s} phase. Contact us via the enquiry form for the latest TOP timeline.`;
    }
    faqs.push({
      question: `When does ${name} TOP (Temporary Occupation Permit)?`,
      answer,
    });
  }

  // 4. Developer
  if (p.project_developer) {
    faqs.push({
      question: `Who is the developer of ${name}?`,
      answer: `${name} is developed by ${p.project_developer}, the appointed Singapore developer responsible for the project's design, construction, and sales.`,
    });
  }

  // 5. Units
  if (p.units && p.units > 0) {
    faqs.push({
      question: `How many units are there at ${name}?`,
      answer: `${name} comprises ${p.units.toLocaleString()} residential units across the development.`,
    });
  }

  // 6. Price range — only render if min is real (>= SGD 10K, same floor
  // we use for schema offers)
  if (p.min_flat_price && p.min_flat_price >= 10_000) {
    const min = fmtMoney(p.min_flat_price);
    const hasMax =
      p.max_flat_price && p.max_flat_price > p.min_flat_price;
    const rangeText = hasMax
      ? `start from ${min} and range up to ${fmtMoney(p.max_flat_price!)}`
      : `start from ${min}`;
    faqs.push({
      question: `What's the price range at ${name}?`,
      answer: `Indicative prices at ${name} ${rangeText}. Final prices are confirmed at booking and vary by unit type, floor, and view; contact us via the enquiry form for the latest unit-by-unit price list.`,
    });
  } else {
    faqs.push({
      question: `What's the price range at ${name}?`,
      answer: `Official pricing for ${name} has not yet been released by the developer. Register your interest on this page and we'll send the price list the moment it's published.`,
    });
  }

  // 7. Showflat (CTA — always render)
  faqs.push({
    question: `What's the showflat location for ${name}?`,
    answer: `The showflat for ${name} is reserved for registered viewers. Sign up via the enquiry form on this page and we'll send you the Google Maps location, viewing times, and direct contact for VIP showflat access. Walk-ins without an appointment are typically not permitted.`,
  });

  // 8. Comparison
  faqs.push({
    question: `Can I compare ${name} with other Singapore developments?`,
    answer: `Yes. Tap the Compare Property button on this page to place ${name} side-by-side with up to three other developments. The comparison covers price, unit sizes, tenure, developer, and TOP.`,
  });

  // 10. Discounts
  faqs.push({
    question: `Are there developer discounts available at ${name}?`,
    answer: `Developer-issued discounts at ${name} change with each phase of release. Speak with our licensed agent via the enquiry form on this page for the latest discount structure, early-bird pricing, and any stamp duty subsidies on offer.`,
  });

  // 11. Direct developer negotiation
  faqs.push({
    question: `Can I negotiate directly with the developer of ${name}?`,
    answer: `All new launch transactions in Singapore are routed through CEA-licensed salespersons. AI Property represents buyers in negotiation with the developer of ${name}. To engage us, email hello@aiproperty.sg with your NRIC (front and back), contact number, email address, and the unit number you're interested in.`,
  });

  // 12. VIP viewing
  faqs.push({
    question: `How do I register for a VIP viewing of ${name}?`,
    answer: `Submit your contact details via the registration form on this page and our agent will arrange a personalised VIP viewing of ${name}. VIP viewings cover the showflat, available units, floor plans, and developer pricing not yet public.`,
  });

  // 13. Floorplans + project info
  faqs.push({
    question: `Do you have floor plans and project information for ${name}?`,
    answer: `Available floor plans, schematics, unit mix breakdowns, and the project factsheet for ${name} are published on this page as soon as they're released by the developer. Sign up for project updates and you'll be notified the moment new materials go live.`,
  });

  // 14-20: Singapore-policy + process FAQs.
  // Each answer is citation-friendly for AI Overviews / ChatGPT /
  // Perplexity — specific numbers, named processes, structured prose.
  // Keep numeric facts (ABSD/BSD/LTV/TDSR) in sync with current IRAS
  // and MAS rates. If policy changes, update here and the next deploy
  // ships fresh schema for every property.
  //
  // An EC is not a private condo, so the policy block below would be
  // wrong on an EC page: foreigners cannot buy a new EC at all, a
  // household of PRs cannot either, there is an income ceiling, and an
  // MOP applies. EC pages get their own block instead.
  if (p.isExecutiveCondo ?? isExecutiveCondo(p)) {
    faqs.push(...executiveCondoFaqs(name), maintenanceFeeFaq(name));
    return faqs;
  }

  // 14. Down payment + LTV
  faqs.push({
    question: `What's the down payment for ${name}?`,
    answer: `For Singapore citizens and PRs buying a private residential property like ${name}, the standard minimum down payment under the 75% loan-to-value (LTV) limit is 25% of the purchase price. The first 5% is paid in cash at booking when the developer issues the Option to Purchase (OTP). The remaining 20% is payable when the OTP is exercised, sourced from cash and/or CPF Ordinary Account. Foreigners follow the same 25% down payment structure but cannot use CPF. Speak to our licensed agent for an individual TDSR calculation.`,
  });

  // 15. ABSD
  faqs.push({
    question: `What's the Additional Buyer's Stamp Duty (ABSD) on ${name}?`,
    answer: `ABSD on a private residential purchase in Singapore including ${name} is 0% for a Singapore citizen's first property, 20% for a citizen's second, and 30% for a third or more. PRs pay 5% on their first property and 30% on their second. Foreigners pay 60% on any property. ABSD remission is available for couples where one spouse is a Singapore citizen, subject to conditions including selling existing properties within 6 months. Final rates are set by IRAS at the time of purchase.`,
  });

  // 16. BSD
  faqs.push({
    question: `What's the Buyer's Stamp Duty (BSD) on ${name}?`,
    answer: `BSD on ${name} is calculated on a tiered sliding scale set by IRAS: 1% on the first S$180,000, 2% on the next S$180,000, 3% on the next S$640,000, 4% on the next S$500,000, 5% on the next S$1.5M, and 6% on the amount above S$3M. BSD is payable to IRAS within 14 days of signing the purchase contract. Any Additional Buyer's Stamp Duty (ABSD) is on top of BSD, not instead of it.`,
  });

  // 17. Booking + OTP process
  faqs.push({
    question: `How does the booking and OTP process work at ${name}?`,
    answer: `To book a unit at ${name}: (1) Register your interest via the enquiry form on this page. (2) Our agent sends the latest price list, floor plans, and unit availability. (3) Confirm your selection and submit the 5% Option Fee (cheque or PayNow). (4) The developer issues the Option to Purchase (OTP), with the Sale & Purchase Agreement typically delivered within 14 days. (5) Exercise the OTP within roughly 3 weeks of receiving the S&PA by paying the remaining 20% (balance of the 25% down payment) and signing the agreement. The full process from booking to S&PA signing usually takes 3 to 6 weeks.`,
  });

  // 18. Maintenance fees
  faqs.push(maintenanceFeeFaq(name));

  // 19. CPF + bank loan
  faqs.push({
    question: `Can I use CPF and a bank loan to buy ${name}?`,
    answer: `Yes. For ${name}, eligible Singapore citizens and PRs can fund the purchase with a combination of cash, CPF Ordinary Account (OA) funds, and a bank loan, subject to the Total Debt Servicing Ratio (TDSR) cap of 55% of gross monthly income. The standard split is 5% cash + 20% CPF or cash for the down payment, with the remaining 75% via a bank loan. MAS allows a maximum private-property loan tenure of 35 years, but tenures over 30 years (or that extend past age 65) face reduced LTV limits. Foreigners cannot use CPF and must rely on cash plus a bank loan.`,
  });

  // 20. New launch vs resale
  faqs.push({
    question: `How is buying ${name} different from buying a resale condo?`,
    answer: `Buying ${name} as a new launch differs from a resale condo purchase in three main ways. First, payment is progressive: instead of paying the full price upfront, payments are staged (5%, 20%, then construction-milestone instalments). Second, new launches ship with the developer's full fittings, warranty, and current building specifications. Third, while Buyer's Stamp Duty and ABSD apply identically to both, new launches give early-bird buyers access to preferred units, floors, and views at the official launch price. Resale condos move faster (immediate possession) but lack the construction-period payment flexibility.`,
  });

  return faqs;
}
